import {
  GridApi,
  CellStyle,
  ColDef,
  GridReadyEvent,
  ValueFormatterParams,
  AgGridEvent,
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  GridColumnsChangedEvent,
  ColumnState,
  ModuleRegistry,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { LicenseManager } from '@ag-grid-enterprise/core';
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import classNames from 'classnames/bind';
import { AGTableProps } from '@creditsights/styleguide-web';
import { pick } from 'lodash';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useWindowResizeListener } from '../../hooks/common';
import styles from './FullPage.module.scss';
import './AGTable.scss';

ModuleRegistry.registerModules([ExcelExportModule, ServerSideRowModelModule]);
LicenseManager.setLicenseKey(
  'CompanyName=Fitch Group, Inc.,LicensedGroup=Fitch Group-Ratings Products-CSM,LicenseType=MultipleApplications,LicensedConcurrentDeveloperCount=2,LicensedProductionInstancesCount=2,AssetReference=AG-027241,ExpiryDate=15_June_2023_[v2]_MTY4Njc4MzYwMDAwMA==c8074d6a4d7eb14bfe4e203ce2742b18'
);

const cx = classNames.bind(styles);

export const defaultValueFormatter = <TData,>(params: ValueFormatterParams<TData>): string =>
  params.value || params.value === 0 ? params.value : '—';

type PartialColumnState = Pick<ColumnState, 'colId' | 'pinned' | 'width'>;
const partialColumnStateKeys: (keyof PartialColumnState)[] = ['colId', 'pinned', 'width'];

export const persistColumnState = (columnPersistenceKey: string, columnState: ColumnState[]): void => {
  const partialColumnState: PartialColumnState[] = columnState.map((state) => pick(state, partialColumnStateKeys));
  localStorage.setItem(columnPersistenceKey, JSON.stringify(partialColumnState));
};

export const defaultColumnDefinition: ColDef = {
  resizable: true,
  sortable: true,
  editable: false,
  minWidth: 100,
  valueFormatter: defaultValueFormatter,
  cellStyle: (params): CellStyle | null => {
    if (typeof params.value === 'number') {
      return { textAlign: 'right' };
    }
    return null;
  },
};

export interface FullPageTableProps<TData> extends AGTableProps<TData> {
  pageTitle: ReactNode;
  paginationComponent?: React.ReactElement;
  headerComponent?: React.ReactElement;
}
const DEFAULT_NAVBAR_HEIGHT = 104;
const getApplicationHeaderHeight = () =>
  document.getElementsByTagName('header')[0]?.clientHeight ?? DEFAULT_NAVBAR_HEIGHT;

const FullPageTable = <TData,>({
  pageTitle,
  onGridReady,
  headerComponent,
  additionalDefaultColDef,
  columnPersistenceKey,
  onColumnMoved,
  onColumnPinned,
  onColumnResized,
  onGridColumnsChanged,
  ...props
}: FullPageTableProps<TData>): React.ReactElement => {
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const agGridViewportRef = useRef<HTMLDivElement>();
  const gridApiRef = useRef<GridApi>();
  const lastTableContainerTopRef = useRef<number>();
  const lastTableContainerHeightRef = useRef<number>();

  const saveColumnState = useCallback(
    (event: AgGridEvent) => {
      if (columnPersistenceKey) {
        persistColumnState(columnPersistenceKey, event.columnApi.getColumnState());
      }
    },
    [columnPersistenceKey]
  );

  const handleColumnMoved = useCallback((event: ColumnMovedEvent) => {
    // @ts-ignore
    onColumnMoved?.(event);
    saveColumnState(event);
  }, []);

  const handleColumnPinned = useCallback((event: ColumnPinnedEvent) => {
    // @ts-ignore
    onColumnPinned?.(event);
    saveColumnState(event);
  }, []);

  const handleColumnResized = useCallback((event: ColumnResizedEvent) => {
    // @ts-ignore
    onColumnResized?.(event);
    saveColumnState(event);
  }, []);

  const handleGridColumnsChanged = useCallback((event: GridColumnsChangedEvent) => {
    // @ts-ignore
    onGridColumnsChanged?.(event);
    saveColumnState(event);
    resizeColumnsToFit();
  }, []);

  const resizeColumnsToFit = useCallback(() => {
    gridApiRef.current?.sizeColumnsToFit();
  }, []);

  useWindowResizeListener(() => resizeColumnsToFit());

  useEffect(() => {
    resizeColumnsToFit();
  }, [props.columnDefs]);

  const calcBlocks = () => {
    if (tableWrapperRef.current && tableContainerRef.current) {
      const tableTop = tableWrapperRef.current.getBoundingClientRect().top;
      const applicationHeaderHeight = getApplicationHeaderHeight();

      if (lastTableContainerTopRef.current !== applicationHeaderHeight) {
        lastTableContainerTopRef.current = applicationHeaderHeight;
        tableContainerRef.current.style.top = `${applicationHeaderHeight}px`;
      }

      const nextTableContainerTop = Math.max(applicationHeaderHeight, tableTop);
      if (lastTableContainerHeightRef.current !== nextTableContainerTop) {
        lastTableContainerHeightRef.current = nextTableContainerTop;
        tableContainerRef.current.style.height = `calc(100vh - ${nextTableContainerTop}px - 100px)`;
      }
    }
  };

  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      gridApiRef.current = event.api;
      // @ts-ignore
      onGridReady?.(event);

      agGridViewportRef.current = tableWrapperRef.current
        ?.getElementsByClassName('ag-body-viewport')
        ?.item(0) as HTMLDivElement;

      const scrollbarEl = tableWrapperRef.current
        ?.getElementsByClassName('ag-body-vertical-scroll')
        ?.item(0) as HTMLDivElement;

      if (scrollbarEl) {
        // scrollbarEl.style.display = 'none';
      }

      if (agGridViewportRef.current) {
        // agGridViewportRef.current.style.overflowY = 'hidden';
      }

      resizeColumnsToFit();
      calcBlocks();
    },
    [onGridReady]
  );

  // useEffect(() => {
  //   calcBlocks();
  //
  //   // if (tableWrapperRef.current && gridApiRef.current) {
  //   //   const sizes = gridApiRef.current.getSizesForCurrentTheme();
  //   //   const height = sizes.rowHeight * (gridApiRef.current?.getModel()?.getRowCount() || 1) + sizes.headerHeight;
  //   //   const minHeight = tableContainerRef.current?.clientHeight || 0;
  //   //
  //   //   tableWrapperRef.current.style.height = `${Math.max(minHeight, height)}px`;
  //   // }
  // }, [totalResults, rowData]);

  // useEffect(() => {
  //   let lastKnownScrollTopPosition = 0;
  //   let ticking = false;
  //
  //   const listener = () => {
  //     lastKnownScrollTopPosition = window.scrollY;
  //
  //     if (!ticking) {
  //       window.requestAnimationFrame(() => {
  //         calcBlocks();
  //
  //         if (tableWrapperRef.current && agGridViewportRef.current) {
  //           const tableTop = tableWrapperRef.current.getBoundingClientRect().top;
  //           const top = tableTop + lastKnownScrollTopPosition;
  //           const applicationHeaderHeight = getApplicationHeaderHeight();
  //
  //           agGridViewportRef.current.scrollTo({
  //             top: Math.max(0, lastKnownScrollTopPosition - top + applicationHeaderHeight),
  //           });
  //         }
  //
  //         ticking = false;
  //       });
  //
  //       ticking = true;
  //     }
  //   };
  //
  //   window.addEventListener('scroll', listener);
  //
  //   return () => window.removeEventListener('scroll', listener);
  // }, []);

  const defaultColDef = useMemo(
    () => ({
      ...defaultColumnDefinition,
      ...additionalDefaultColDef,
    }),
    [additionalDefaultColDef]
  );

  return (
    <div className={cx('body')}>
      <div className={cx('full-page-container')}>
        <div className={cx('content-container')}>
          <div className={cx('header')}>
            <h1>{pageTitle}</h1>
            <div className={cx('sub-header')}>{headerComponent}</div>
          </div>
          <div className={cx('content')}>
            <div ref={tableWrapperRef}>
              <div
                className={classNames(cx('table-container'), 'ag-theme-alpine')}
                data-testid="table"
                ref={tableContainerRef}
              >
                <AgGridReact
                  className="ag-table"
                  onGridReady={handleGridReady}
                  // @ts-ignore
                  defaultColDef={defaultColDef}
                  headerHeight={40}
                  rowHeight={33}
                  suppressCellFocus
                  suppressMultiSort
                  suppressDragLeaveHidesColumns
                  suppressContextMenu
                  enableCellTextSelection
                  unSortIcon
                  maintainColumnOrder
                  onColumnMoved={handleColumnMoved}
                  onColumnPinned={handleColumnPinned}
                  onColumnResized={handleColumnResized}
                  onGridColumnsChanged={handleGridColumnsChanged}
                  {...props}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullPageTable;
